import React, { useCallback, useMemo } from 'react';
import {
  valueToPixel,
  pixelToValue,
  generateTicks,
  generateLabels,
  formatValue,
  defaultNumberLineConfig,
} from './numberLineUtils';

/**
 * Base SVG component for rendering the number line
 */
const NumberLine = ({
  config = defaultNumberLineConfig,
  elements = [],
  pendingValue = null,
  mode = 'point',
  readOnly = false,
  width = 600,
  height = 100,
  padding = 40,
  onLineClick,
  onElementClick,
  selectedId = null,
  showCorrect = false,
  correctElements = [],
  studentElements = [],
  isCorrect = null,
}) => {
  const mergedConfig = { ...defaultNumberLineConfig, ...config };
  const { min, max, showTicks, showLabels } = mergedConfig;

  const lineY = height / 2;
  const tickHeight = 10;
  const labelOffset = 25;

  const ticks = useMemo(() => generateTicks(mergedConfig), [mergedConfig]);
  const labels = useMemo(() => generateLabels(mergedConfig), [mergedConfig]);

  const toPixel = useCallback(
    (value) => valueToPixel(value, mergedConfig, width, padding),
    [mergedConfig, width, padding]
  );

  const fromPixel = useCallback(
    (pixelX) => pixelToValue(pixelX, mergedConfig, width, padding),
    [mergedConfig, width, padding]
  );

  const handleSvgClick = useCallback(
    (e) => {
      if (readOnly || !onLineClick) return;

      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const value = fromPixel(x);

      // Only process clicks within the valid range
      if (value >= min && value <= max) {
        onLineClick(value);
      }
    },
    [readOnly, onLineClick, fromPixel, min, max]
  );

  // Render a point element
  const renderPoint = (point, isStudent = true, idx) => {
    if (!point || point.value == null) return null;
    const x = toPixel(point.value);
    const isSelected = selectedId === point.id;

    // Determine fill color based on context
    let fill = '#2196f3'; // blue for interactive
    if (showCorrect && isStudent) {
      fill = isCorrect ? '#4caf50' : '#f44336'; // green if correct, red if wrong
    } else if (!isStudent) {
      fill = '#4caf50'; // green for correct answer display
    }

    return (
      <g key={point.id || `point-${idx}`}>
        <circle
          cx={x}
          cy={lineY}
          r={isSelected ? 10 : 8}
          fill={fill}
          stroke={isSelected ? '#000' : 'none'}
          strokeWidth={2}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && onElementClick) {
              onElementClick(point.id);
            }
          }}
        />
        {/* Value label above point */}
        <text
          x={x}
          y={lineY - 15}
          textAnchor="middle"
          fontSize="12"
          fill="#333"
        >
          {point.label ?? formatValue(point.value)}
        </text>
      </g>
    );
  };

  // Render an interval element
  const renderInterval = (interval, isStudent = true, idx) => {
    if (!interval || interval.start == null || interval.end == null) return null;
    const startX = toPixel(interval.start);
    const endX = toPixel(interval.end);
    const isSelected = selectedId === interval.id;

    // Determine fill color based on context
    let fill = '#2196f3';
    let stroke = '#2196f3';
    if (showCorrect && isStudent) {
      fill = isCorrect ? '#4caf50' : '#f44336';
      stroke = isCorrect ? '#4caf50' : '#f44336';
    } else if (!isStudent) {
      fill = '#4caf50';
      stroke = '#4caf50';
    }

    return (
      <g key={interval.id || `interval-${idx}`}>
        {/* Line segment */}
        <line
          x1={startX}
          y1={lineY}
          x2={endX}
          y2={lineY}
          stroke={stroke}
          strokeWidth={isSelected ? 6 : 4}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && onElementClick) {
              onElementClick(interval.id);
            }
          }}
        />
        {/* Start endpoint */}
        <circle
          cx={startX}
          cy={lineY}
          r={8}
          fill={interval.startOpen ? '#fff' : fill}
          stroke={stroke}
          strokeWidth={2}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && onElementClick) {
              onElementClick(interval.id, 'start');
            }
          }}
        />
        {/* End endpoint */}
        <circle
          cx={endX}
          cy={lineY}
          r={8}
          fill={interval.endOpen ? '#fff' : fill}
          stroke={stroke}
          strokeWidth={2}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && onElementClick) {
              onElementClick(interval.id, 'end');
            }
          }}
        />
        {/* Labels */}
        <text
          x={startX}
          y={lineY - 15}
          textAnchor="middle"
          fontSize="11"
          fill="#333"
        >
          {interval.startLabel ?? formatValue(interval.start)}
        </text>
        <text
          x={endX}
          y={lineY - 15}
          textAnchor="middle"
          fontSize="11"
          fill="#333"
        >
          {interval.endLabel ?? formatValue(interval.end)}
        </text>
      </g>
    );
  };

  // Render a ray element
  const renderRay = (ray, isStudent = true, idx) => {
    if (!ray || ray.origin == null || !ray.direction) return null;
    const originX = toPixel(ray.origin);
    const isSelected = selectedId === ray.id;

    // Determine fill color based on context
    let fill = '#2196f3';
    let stroke = '#2196f3';
    if (showCorrect && isStudent) {
      fill = isCorrect ? '#4caf50' : '#f44336';
      stroke = isCorrect ? '#4caf50' : '#f44336';
    } else if (!isStudent) {
      fill = '#4caf50';
      stroke = '#4caf50';
    }

    // Calculate the extent of the ray
    const extentX = ray.direction === 'left' ? padding - 10 : width - padding + 10;

    return (
      <g key={ray.id || `ray-${idx}`}>
        {/* Line segment */}
        <line
          x1={originX}
          y1={lineY}
          x2={extentX}
          y2={lineY}
          stroke={stroke}
          strokeWidth={isSelected ? 6 : 4}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && onElementClick) {
              onElementClick(ray.id);
            }
          }}
        />
        {/* Arrow at the end */}
        <polygon
          points={
            ray.direction === 'left'
              ? `${extentX},${lineY} ${extentX + 12},${lineY - 6} ${extentX + 12},${lineY + 6}`
              : `${extentX},${lineY} ${extentX - 12},${lineY - 6} ${extentX - 12},${lineY + 6}`
          }
          fill={stroke}
        />
        {/* Origin endpoint */}
        <circle
          cx={originX}
          cy={lineY}
          r={8}
          fill={ray.originOpen ? '#fff' : fill}
          stroke={stroke}
          strokeWidth={2}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && onElementClick) {
              onElementClick(ray.id, 'origin');
            }
          }}
        />
        {/* Label */}
        <text
          x={originX}
          y={lineY - 15}
          textAnchor="middle"
          fontSize="11"
          fill="#333"
        >
          {ray.originLabel ?? formatValue(ray.origin)}
        </text>
      </g>
    );
  };

  // Render element based on type
  const renderElement = (el, isStudent = true, idx) => {
    switch (el.type) {
      case 'point':
        return renderPoint(el, isStudent, idx);
      case 'interval':
        return renderInterval(el, isStudent, idx);
      case 'ray':
        return renderRay(el, isStudent, idx);
      default:
        return null;
    }
  };

  // Render pending value indicator
  const renderPending = () => {
    if (pendingValue === null) return null;

    const x = toPixel(pendingValue);
    return (
      <g>
        <circle
          cx={x}
          cy={lineY}
          r={8}
          fill="#ffeb3b"
          stroke="#ffc107"
          strokeWidth={2}
          strokeDasharray="4,2"
        />
        <text
          x={x}
          y={lineY - 15}
          textAnchor="middle"
          fontSize="11"
          fill="#f57c00"
        >
          {formatValue(pendingValue)}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      onClick={handleSvgClick}
      style={{
        cursor: readOnly ? 'default' : 'crosshair',
        backgroundColor: '#fafafa',
        borderRadius: '4px',
        border: '1px solid #e0e0e0',
      }}
    >
      {/* Main axis line */}
      <line
        x1={padding}
        y1={lineY}
        x2={width - padding}
        y2={lineY}
        stroke="#333"
        strokeWidth={2}
      />

      {/* Arrow at left end */}
      <polygon
        points={`${padding - 10},${lineY} ${padding + 2},${lineY - 6} ${padding + 2},${lineY + 6}`}
        fill="#333"
      />

      {/* Arrow at right end */}
      <polygon
        points={`${width - padding + 10},${lineY} ${width - padding - 2},${lineY - 6} ${width - padding - 2},${lineY + 6}`}
        fill="#333"
      />

      {/* Tick marks */}
      {showTicks &&
        ticks.map((value, i) => {
          const x = toPixel(value);
          return (
            <line
              key={`tick-${i}`}
              x1={x}
              y1={lineY - tickHeight / 2}
              x2={x}
              y2={lineY + tickHeight / 2}
              stroke="#333"
              strokeWidth={1}
            />
          );
        })}

      {/* Labels */}
      {showLabels &&
        labels.map((value, i) => {
          const x = toPixel(value);
          return (
            <text
              key={`label-${i}`}
              x={x}
              y={lineY + labelOffset}
              textAnchor="middle"
              fontSize="12"
              fill="#666"
            >
              {formatValue(value)}
            </text>
          );
        })}

      {/* Correct elements (shown in read-only/comparison mode) */}
      {showCorrect &&
        correctElements.map((el, i) => renderElement(el, false, i))}

      {/* Student/interactive elements */}
      {(showCorrect ? studentElements : elements).map((el, i) =>
        renderElement(el, true, i)
      )}

      {/* Pending value indicator */}
      {renderPending()}
    </svg>
  );
};

export default NumberLine;
